"use client"

import { useState, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Code2, X, RotateCcw, Eye } from "lucide-react"
import { testScenarios, scenarioLabels, getScenarioKeys, Event as ScenarioEvent } from "@/lib/test-scenarios"

interface ScenarioSelectorProps {
  currentScenario: string
  onScenarioChange: (scenario: string) => void
  onClose: () => void
  event: ScenarioEvent
}

export function ScenarioSelector({
  currentScenario,
  onScenarioChange,
  onClose,
  event,
}: ScenarioSelectorProps) {
  const [isMinimized, setIsMinimized] = useState(false)

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <Card className="border-2 border-dashed border-yellow-500 bg-yellow-50/95 backdrop-blur-sm shadow-xl">
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Code2 className="h-4 w-4 text-yellow-700" />
              <Label className="text-sm font-semibold text-yellow-900">
                מצב בדיקה
              </Label>
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMinimized(!isMinimized)}
                className="h-6 w-6 p-0 hover:bg-yellow-200"
              >
                <Eye className={`h-3 w-3 ${isMinimized ? "opacity-50" : ""}`} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-6 w-6 p-0 hover:bg-yellow-200"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Scenario Selector */}
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs text-yellow-800">בחר תרחיש:</Label>
                  <Select value={currentScenario} onValueChange={onScenarioChange}>
                    <SelectTrigger className="bg-white border-yellow-300">
                      <SelectValue placeholder="בחר תרחיש" />
                    </SelectTrigger>
                    <SelectContent>
                      {getScenarioKeys().map((key) => (
                        <SelectItem key={key} value={key}>
                          {scenarioLabels[key]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Current Status Info */}
                <div className="bg-white rounded-md p-3 space-y-2 border border-yellow-200">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">סטטוס:</span>
                      <p className="font-semibold">{event.status}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">סוג:</span>
                      <p className="font-semibold">{event.type}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">מתנדב:</span>
                      <p className="font-semibold">
                        {event.volunteer ? "הוקצה" : "אין"}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">זמן הגעה:</span>
                      <p className="font-semibold">
                        {event.volunteer?.eta ? `${event.volunteer.eta} דק'` : "—"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Reset Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onScenarioChange("completed")}
                  className="w-full gap-2 bg-white hover:bg-yellow-100 border-yellow-300"
                >
                  <RotateCcw className="h-3 w-3" />
                  חזור לברירת מחדל
                </Button>
              </div>

              {/* Instructions */}
              <div className="mt-3 pt-3 border-t border-yellow-300">
                <p className="text-xs text-yellow-800 space-y-1">
                  <span className="block">💡 טיפ: השתמש ב-Ctrl+Shift+D להסתרה</span>
                  <span className="block">🔄 החלף בין תרחישים לבדיקת התנהגות</span>
                </p>
              </div>
            </>
          )}

          {isMinimized && (
            <div className="text-xs text-yellow-800">
              <p className="font-semibold">{scenarioLabels[currentScenario]}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
